import classNames from "classnames";
import { ReactNode, HTMLAttributes } from "react";

export interface ListItemProps extends HTMLAttributes<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement> {
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  metadata?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  trailing?: "chevron" | ReactNode;
  /** Full-width row rendered below the main content, separated by a divider. For badge/chip groups (status badges, style preference chips) that need their own line. */
  footer?: ReactNode;
  interactive?: boolean;
  density?: "comfortable" | "compact" | "dense";
  variant?: "plain" | "glass" | "card";
  className?: string;
  htmlType?: "div" | "button" | "a";
  href?: string;
}

export const ListItem = ({
  leading,
  title,
  description,
  metadata,
  badge,
  actions,
  trailing,
  footer,
  interactive = false,
  density = "comfortable",
  variant = "plain",
  className,
  htmlType = "div",
  href,
  ...props
}: ListItemProps) => {
  const Component = href ? "a" : htmlType;

  const densityClasses = {
    comfortable: "p-4 md:p-5 gap-4",
    compact: "p-3 md:p-4 gap-3",
    dense: "p-2 gap-2",
  };

  const interactiveClasses = interactive || href || htmlType === "button"
    ? "cursor-pointer hover:bg-primary-50/50 dark:hover:bg-primary-900/20 active:bg-primary-100/50 dark:active:bg-primary-900/40 transition-colors"
    : "";

  const variantClasses = {
    plain: "",
    glass: "rounded-2xl border border-white/20 dark:border-primary-800/30 bg-white/20 dark:bg-content-900/25 backdrop-blur-xl shadow-sm",
    card: "rounded-xl border border-primary-100 bg-white/50 dark:border-primary-800/60 dark:bg-content-900/40 backdrop-blur-md",
  };

  return (
    <Component
      href={href}
      className={classNames(
        "flex flex-col w-full text-left font-sans text-content-900 dark:text-content-100 group outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-inset",
        densityClasses[density],
        interactiveClasses,
        variantClasses[variant],
        className
      )}
      {...props as any}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 w-full">
        <div className="flex flex-1 items-start md:items-center gap-3 md:gap-4 min-w-0 w-full">
          {leading && (
            <div className="shrink-0">
              {leading}
            </div>
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={classNames(
                "font-medium truncate",
                density === "dense" ? "text-sm" : "text-base"
              )}>
                {title}
              </span>
              {badge && <div className="shrink-0">{badge}</div>}
            </div>
            {description && (
              <span className={classNames(
                "text-content-500 dark:text-content-400 line-clamp-1 mt-0.5",
                density === "dense" ? "text-xs" : "text-sm"
              )}>
                {description}
              </span>
            )}
            {metadata && (
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-content-400 dark:text-content-500">
                {metadata}
              </div>
            )}
          </div>
        </div>

        {(actions || trailing) && (
          <div className={classNames(
            "flex items-center shrink-0 justify-end w-full md:w-auto gap-2"
          )}>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
            {trailing === "chevron" ? (
              <div className="icon-[ph--caret-right] text-content-400 group-hover:text-primary-500 transition-colors shrink-0 ml-2 h-5 w-5" />
            ) : (
              trailing
            )}
          </div>
        )}
      </div>

      {footer && (
        <div className="w-full border-t border-content-100 dark:border-content-800 pt-3">
          {footer}
        </div>
      )}
    </Component>
  );
};
